import { createRouter, createWebHistory } from "vue-router";
import { useMainStore } from "../stores/main";

// Pages that only exist for impact drivers; sent home when the org has them off.
const IMPACT_DRIVER_ROUTES = {
  account_planning: "home",
  report: "home",
  embedded_account_planning: "embedded_home",
};

function lazyLoad(importFn) {
  return () =>
    importFn().catch((err) => {
      if (
        err.message?.includes("dynamically imported module") ||
        err.message?.includes("Importing a module script failed")
      ) {
        window.location.reload();
        return; // prevent throw after reload
      }
      throw err;
    });
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: lazyLoad(() => import("../views/Index.vue")),
    },
    {
      path: "/account-planning/:customer_crm_id(\\d+)?",
      name: "account_planning",
      component: lazyLoad(() => import("../views/AccountPlan.vue")),
      props: true,
    },
    {
      path: "/settings",
      name: "settings",
      component: lazyLoad(() => import("../views/Settings.vue")),
    },
    {
      path: "/notifications",
      name: "notifications",
      component: lazyLoad(() => import("../views/Notifications.vue")),
    },
    {
      path: "/onboarding",
      name: "onboarding",
      component: lazyLoad(() => import("../views/GetStarted.vue")),
    },
    {
      path: "/embedded",
      name: "embedded_home",
      component: lazyLoad(() => import("../views/Index.vue")),
    },
    {
      path: "/embedded/account-planning/:customer_crm_id(\\d+)?",
      name: "embedded_account_planning",
      component: lazyLoad(() => import("../views/AccountPlan.vue")),
      props: true,
    },
    {
      path: "/dashboard",
      name: "dashboard",
      component: () => import("../views/Dashboard.vue"),
    },
    {
      path: "/embedded/dashboard",
      name: "embedded_dashboard",
      component: lazyLoad(() => import("../views/Dashboard.vue")),
    },
    {
      path: "/charts",
      name: "charts",
      component: lazyLoad(() => import("../views/Charts.vue")),
    },
    {
      path: "/charts/:id",
      name: "view_chart",
      component: lazyLoad(() => import("../views/ViewChart.vue")),
    },
    {
      path: "/report",
      name: "report",
      component: lazyLoad(() => import("../views/Report.vue")),
    },
    {
      path: "/signals",
      name: "signals",
      component: lazyLoad(() => import("../views/SignalsView.vue")),
    },
    {
      path: "/processes",
      name: "processes",
      component: lazyLoad(() => import("../views/ProcessesView.vue")),
    },
    {
      path: "/processes/:id(\\d+)/edit",
      name: "process_builder",
      component: lazyLoad(() => import("../views/ProcessBuilderView.vue")),
      props: true,
    },
    {
      path: "/embedded/signals",
      name: "embedded_signals",
      component: lazyLoad(() => import("../views/SignalsView.vue")),
    },
    {
      path: "/embedded/processes",
      name: "embedded_processes",
      component: lazyLoad(() => import("../views/ProcessesView.vue")),
    },
  ],
});

router.beforeEach(async (to) => {
  if (to.name === "process_builder") {
    const store = useMainStore();
    try {
      await store.ensureUserData();
    } catch (error) {
      return { name: "processes" };
    }
    if (!store.userIsManagerOrAdmin) return { name: "processes" };
  }

  const fallback = IMPACT_DRIVER_ROUTES[to.name];
  if (!fallback) return true;
  const flags = await useMainStore().ensureFeatureFlags();
  return flags.impactDriversDisabled ? { name: fallback } : true;
});

export default router;
